import React, { useState } from "react";
import { CancelManager } from "../../lib/cancelManager.ts";
import { Box, Button, LinearProgress, Modal, Typography } from "@mui/material";

export type ExportProgressStatus = "Export" | "Success" | "Error" | "Cancel";

interface ExportProgressModalProps {
  open: boolean;
  onClose: () => void;
  totalCount: number;
  downloadedCount: number;
  fetchedCount?: number;
  progressInfo: { status: ExportProgressStatus; message: string };
}

// エクスポート進捗を表示するモーダルコンポーネント
export const ExportProgressModal: React.FC<ExportProgressModalProps> = ({
  open,
  onClose,
  totalCount,
  downloadedCount,
  fetchedCount = 0,
  progressInfo,
}) => {
  const [isCancel, setIsCancel] = useState<boolean>(false);
  // キャンセルボタンのクリック時に呼び出される関数
  const handleCancelImport = () => {
    const cancelManager = CancelManager.getInstance();
    cancelManager.cancel();
    setIsCancel(true);
  };
  // 閉じるボタンのクリック時に呼び出される関数
  const handleClose = () => {
    setIsCancel(false);
    onClose();
  };

  const getStatusColor = (status: ExportProgressStatus) => {
    switch (status) {
      case "Success":
        return "success.main";
      case "Error":
        return "error.main";
      case "Cancel":
        return "warning.main";
      default:
        return "text.primary";
    }
  };

  const getStatusText = (status: ExportProgressStatus) => {
    switch (status) {
      case "Success":
        return "完了";
      case "Error":
        return "エラー";
      case "Cancel":
        return "キャンセル";
      case "Export":
        return "エクスポート中";
    }
  };

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          outline: "none",
        }}
      >
        <Typography variant="h6" component="h2">
          データエクスポート進捗
        </Typography>
        {progressInfo.status === "Export" && totalCount === 0 && !isCancel ? (
          <>
            <LinearProgress variant="indeterminate" sx={{ my: 2 }} />
            <Typography variant="body1">
              {fetchedCount > 0 ? `データをスキャン中... (${fetchedCount} 件)` : "データを取得しています..."}
            </Typography>
          </>
        ) : progressInfo.status === "Export" && totalCount > 0 ? (
          <>
            <LinearProgress
              variant="determinate"
              value={(downloadedCount / totalCount) * 100}
              sx={{ my: 2 }}
            />
            <Typography variant="body1">
              {downloadedCount}/{totalCount} 件処理済
            </Typography>
          </>
        ) : isCancel && progressInfo.status === "Export" ? (
          <>
            <LinearProgress variant="determinate" value={0} sx={{ my: 2, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ color: "warning.main" }}>
              キャンセル中...
            </Typography>
          </>
        ) : (
          <LinearProgress
            variant="determinate"
            value={totalCount > 0 ? (downloadedCount / totalCount) * 100 : 0}
            sx={{ my: 2 }}
          />
        )}
        <Typography
          variant="body1"
          sx={{ color: getStatusColor(progressInfo.status) }}
        >
          {getStatusText(progressInfo.status)} : {progressInfo.message}
        </Typography>

        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button
            variant="contained"
            onClick={handleCancelImport}
            disabled={(totalCount > 0 && totalCount === downloadedCount) || isCancel || progressInfo.status === "Success" || progressInfo.status === "Error"}
            sx={{ mr: 2 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleClose}
            disabled={progressInfo.status === "Export" && !isCancel}
          >
            閉じる
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
